import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { API_ENDPOINTS, getApiBaseUrl } from '../config/api';
import { getAppInsights } from '../config/telemetry';

const PaymentPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [failedEndpoint, setFailedEndpoint] = useState('');

  useEffect(() => {
    const userData = location.state?.userData;

    if (!userData) {
      setError('No user data provided. Please start from the home page.');
      setLoading(false);
      return;
    }

    processPayment(userData);
  }, [location.state]);

  const processPayment = async (userData) => {
    const endpoint = API_ENDPOINTS.PROCESS_PAYMENT;
    try {
      setLoading(true);

      const response = await fetch(endpoint, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          firstName: userData.firstName,
          lastName: userData.lastName,
          email: userData.email,
        }),
      });

      const contentType = response.headers.get('content-type');
      const isJson = contentType && contentType.includes('application/json');

      if (!response.ok) {
        const status = response.status;
        const rawDetail = isJson
          ? (await response.json().catch(() => null))?.message
          : await response.text().catch(() => null);

        // Always log full details to console for debugging.
        console.error('[PaymentPage] server error response:', { endpoint, status, rawDetail });

        // In production, hide raw server internals from end users.
        const isServerError = status >= 500;
        const userDetail =
          process.env.NODE_ENV !== 'production' || !isServerError
            ? (rawDetail || `Server error (HTTP ${status})`)
            : `The payment service is temporarily unavailable (HTTP ${status}). Please try again or contact support.`;

        throw new Error(userDetail);
      }

      if (!isJson) {
        await response.text();
        throw new Error(
          `Unexpected response from server (HTTP ${response.status}). Please contact support.`
        );
      }

      const data = await response.json();

      if (!data.checkoutUrl) {
        throw new Error('Payment checkout URL not received from server');
      }

      const caseInfoData = {
        ...userData,
        uniqueTransId: data.uniqueTransId,
        localRef: data.localRef,
        token: data.token
      };

      sessionStorage.setItem('dui_payment_data', JSON.stringify(caseInfoData));

      window.location.assign(data.checkoutUrl);
      
    } catch (err) {
      console.error('[PaymentPage] processPayment failed:', { endpoint, error: err });
      const ai = getAppInsights();
      if (ai) ai.trackException({ exception: err, properties: { component: 'PaymentPage', endpoint } });
      setFailedEndpoint(endpoint);
      setError(`Payment processing failed: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleGoHome = () => {
    navigate('/');
  };

  return (
    <div className="payment-page">
      <h1>Payment Page Loading</h1>

      {loading && (
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          padding: '2rem',
          flexDirection: 'column',
          gap: '1rem'
        }}>
          <span role="img" aria-label="loading">🔄</span> 
          <span>Preparing your secure payment session...</span>
          {/* <span>You will be redirected to the payment gateway automatically.</span> */}
        </div>
      )}

      {error && (
        <div className="error-container" style={{
          backgroundColor: '#ffe6e6',
          border: '1px solid #ff9999',
          borderRadius: '4px',
          padding: '1rem',
          margin: '1rem 0'
        }}>
          <h3>❌ Error</h3>
          <p>{error}</p>
          <p style={{ fontSize: '0.8rem', color: '#555', marginTop: '0.5rem', fontFamily: 'monospace', wordBreak: 'break-all' }}>
            <strong>API base path:</strong> {getApiBaseUrl()}<br />
            <strong>Endpoint called:</strong> {failedEndpoint || API_ENDPOINTS.PROCESS_PAYMENT}
          </p>
          <button onClick={handleGoHome} style={{
            backgroundColor: '#007cba',
            color: 'white',
            border: 'none',
            padding: '0.5rem 1rem',
            borderRadius: '4px',
            cursor: 'pointer'
          }}>
            Return to Home Page
          </button>
        </div>
      )}
    </div>
  );
};

export default PaymentPage;